import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '@picsio/ui';

import Icon from '../Icon';
import Presets from '../DialogPresets';
import Settings from '../DialogPresets/Settings';
import normalizeFormatMimeType from '../../helpers/normalizeFormatMimeType';

import AssetsCopyCropPreview from './AssetsCopyCropPreview';
import AssetsCopyCropAspectPanel from './AssetsCopyCropAspectPanel';

/**
 * Presentational shell shared by AssetsCopyDialog & DownloadDialog.
 * State comes from {@link useAssetConversionForm}, variant-specific strings & labels
 * come from a strategy object. The dialog has two visual layouts:
 *  - split:   image + crop preview on the left, settings on the right (single-item copy/download).
 *  - compact: settings only (bulk / no preview).
 *
 * `extraPanel` is an optional slot that download mode uses for
 * {@link DownloadDialogAdditional} (archive / watermark / collections checkboxes).
 */
export default function ConversionDialogShell({
  form,
  strategy,
  sourceMimeType,
  assets,
  copyPreviewAssetId,
  selectionCount,
  shouldFormatBeDisabled,
  primaryButtonRef,
  extraPanel,
  disablePdfFormat,
}) {
  const {
    presets,
    selectedPresetView,
    selectedSettings,
    isLoading,
    hasPdfProxyPresets,
    cropAspectPreset,
    cropPixelSize,
    cropSourceNaturalSize,
    cropMode,
    cropPreviewRef,
    setNormalizedCrop,
    setCropPixelSize,
    setCropSourceNaturalSize,
    isSplitCropLayout,
    showPresets,
    showConversionSettings,
    showCropControls,
    cropSupported,
    customRatios,
    handleChangePreset,
    handleChangeSettings,
    handleBlurSettings,
    handleSavePreset,
    handleRemovePreset,
    handleCropAspectPresetChange,
    handleCropPixelsCommit,
    handleUserAdjustedCrop,
    handleSubmit,
    handleClose,
  } = form;

  const title = strategy.getTitle({ selectionCount });
  const primaryLabel = strategy.getPrimaryLabel({ selectionCount });
  const cancelLabel = strategy.getCancelLabel({ selectionCount });

  const effectiveSourceMime = sourceMimeType
    ?? (assets?.length === 1 ? normalizeFormatMimeType(assets[0]?.mimeType) : undefined);

  const canSavePreset = Boolean(
    cropSupported
    && (strategy.canSavePreset ? strategy.canSavePreset() : false),
  );

  const presetSelectorLabel = strategy.getPresetSelectorLabel
    ? strategy.getPresetSelectorLabel()
    : undefined;

  const presetsBlock = (
    <If condition={showPresets || hasPdfProxyPresets}>
      <Presets
        isLoading={isLoading}
        selectedPreset={selectedPresetView}
        presets={presets}
        onChange={handleChangePreset}
        onRemove={handleRemovePreset}
        onSave={handleSavePreset}
        canSave={canSavePreset}
        label={presetSelectorLabel}
      />
    </If>
  );

  const renderCropPanel = (variant) => (
    <AssetsCopyCropAspectPanel
      {...(variant === 'bulk' ? { variant: 'bulk' } : {})}
      aspectPreset={cropAspectPreset}
      onAspectPresetChange={handleCropAspectPresetChange}
      disabled={selectedSettings.mimeType === 'original'}
      customRatios={customRatios}
      {...(variant === 'full' ? {
        cropPixelWidth: cropPixelSize?.width,
        cropPixelHeight: cropPixelSize?.height,
        onCropPixelsCommit: handleCropPixelsCommit,
        dimensionsDisabled: cropPixelSize == null || cropAspectPreset === 'original',
        sourceNaturalWidth: cropSourceNaturalSize?.width,
        sourceNaturalHeight: cropSourceNaturalSize?.height,
      } : {})}
    />
  );

  const settingsBlock = (
    <Settings
      selectedSettings={selectedSettings}
      onChange={handleChangeSettings}
      isDisabledFormat={shouldFormatBeDisabled}
      disablePdfFormat={disablePdfFormat}
      onBlur={handleBlurSettings}
      sourceMimeType={effectiveSourceMime}
      omitResizeRow
    />
  );

  const footer = (
    <div className="simpleDialogFooter">
      <Button
        color="secondary"
        size="md"
        variant="contained"
        onClick={handleClose}
      >
        {cancelLabel}
      </Button>
      <Button
        color="primary"
        size="md"
        variant="contained"
        onClick={handleSubmit}
        disabled={selectedSettings.mimeType === 'custom'}
        ref={primaryButtonRef}
      >
        {primaryLabel}
      </Button>
    </div>
  );

  return (
    <div
      className={`simpleDialog downloadDialog assetsCopyDialog${isSplitCropLayout ? ' assetsCopyDialog--split' : ''}`}
    >
      <div className="simpleDialogUnderlayer" />
      <div className="simpleDialogBox">
        <If condition={!isSplitCropLayout}>
          <div className="simpleDialogHeader">
            <h2 className="simpleDialogTitle">
              <span>{title}</span>
            </h2>
            <button
              className="simpleDialogBtnCross"
              onClick={handleClose}
              type="button"
            >
              <Icon name="close" />
            </button>
          </div>
        </If>

        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <Choose>
              <When condition={isSplitCropLayout}>
                <div className="assetsCopyDialog__mainRow">
                  <div className="assetsCopyDialog__cropCol">
                    <AssetsCopyCropPreview
                      ref={cropPreviewRef}
                      variant="splitPreview"
                      previewAssetId={copyPreviewAssetId}
                      sourceMimeType={effectiveSourceMime}
                      aspectPreset={cropAspectPreset}
                      cropMode={cropMode}
                      onNormalizedCropChange={setNormalizedCrop}
                      onCropPixelSizeChange={setCropPixelSize}
                      onSourceNaturalSizeChange={setCropSourceNaturalSize}
                      onUserAdjustedCrop={handleUserAdjustedCrop}
                      lockResize={Array.isArray(customRatios) && customRatios.length > 0}
                    />
                  </div>
                  <div className="assetsCopyDialog__settingsCol">
                    <div className="simpleDialogHeader">
                      <h2 className="simpleDialogTitle">{title}</h2>
                      <button
                        className="simpleDialogBtnCross"
                        onClick={handleClose}
                        type="button"
                      >
                        <Icon name="close" />
                      </button>
                    </div>
                    {presetsBlock}
                    <If condition={showConversionSettings}>
                      {renderCropPanel('full')}
                      {settingsBlock}
                    </If>
                    {extraPanel}
                    {footer}
                  </div>
                </div>
              </When>
              <Otherwise>
                {presetsBlock}
                <If condition={showConversionSettings}>
                  <If condition={showCropControls}>
                    {renderCropPanel('bulk')}
                  </If>
                  {settingsBlock}
                </If>
                {extraPanel}
              </Otherwise>
            </Choose>
          </div>
        </div>

        <If condition={!isSplitCropLayout}>
          {footer}
        </If>
      </div>
    </div>
  );
}

ConversionDialogShell.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  form: PropTypes.object.isRequired,
  strategy: PropTypes.shape({
    getTitle: PropTypes.func.isRequired,
    getPrimaryLabel: PropTypes.func.isRequired,
    getCancelLabel: PropTypes.func.isRequired,
    canSavePreset: PropTypes.func,
    getPresetSelectorLabel: PropTypes.func,
  }).isRequired,
  sourceMimeType: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  assets: PropTypes.arrayOf(PropTypes.object),
  copyPreviewAssetId: PropTypes.string,
  selectionCount: PropTypes.number.isRequired,
  shouldFormatBeDisabled: PropTypes.bool,
  primaryButtonRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  extraPanel: PropTypes.node,
  disablePdfFormat: PropTypes.bool,
};

ConversionDialogShell.defaultProps = {
  sourceMimeType: undefined,
  assets: null,
  copyPreviewAssetId: undefined,
  shouldFormatBeDisabled: false,
  primaryButtonRef: undefined,
  extraPanel: null,
  disablePdfFormat: false,
};
